import { Injectable, signal, computed, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus, SilentRequest, PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';

import { environment } from '@environments/environment';
import { SecurityData } from '../interfaces/security.model';
import { SecurityService } from './security.service';
import { LoadingService } from '../../shared/services/loading.service';

type AuthStatus = 'checking' | 'authenticated' | 'not-authenticated';

type AppUser = {
  name: string | null;
  username: string | null;
  producerNumber: number;
  isInternal: boolean;
  claims?: Record<string, any>;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly msal = inject(MsalService);
  private readonly msalBroadcast = inject(MsalBroadcastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly securityService = inject(SecurityService);
  private readonly loadingService = inject(LoadingService);

  // Instancia manual para Entra ID (AAD)
  private readonly aadMsalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.aad.clientId,
      authority: environment.aad.authority,
      redirectUri: window.location.origin + '/',
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });

  private _authStatus = signal<AuthStatus>('checking');
  private _user = signal<AppUser | null>(null);
  private _isInitialized = signal<boolean>(false);
  private _permissions = signal<SecurityData | null>(null);
  private _permissionsLoaded = signal<boolean>(false);
  private _isLoadingPermissions = false;

  authStatus = computed<AuthStatus>(() => this._authStatus());
  user = computed<AppUser | null>(() => this._user());
  isInitialized = computed<boolean>(() => this._isInitialized());
  permissions = computed<SecurityData | null>(() => this._permissions());
  permissionsLoaded = computed<boolean>(() => this._permissionsLoaded());

  // Módulos
  readonly hasCotizacionesModule = computed(() => this.hasModuleAccess('COTIZACIONES'));
  readonly hasCobrosModule = computed(() => this.hasModuleAccess('COBROS'));
  readonly hasPagosModule = computed(() => this.hasModuleAccess('PAGOS'));
  readonly hasEstadisticasModule = computed(() => this.hasModuleAccess('ESTADISTICAS'));
  readonly hasReporteModule = computed(() => this.hasModuleAccess('REPORTE'));
  readonly hasPolizasModule = computed(() => this.hasModuleAccess('POLIZAS'));
  readonly hasReclamacionesModule = computed(() => this.hasModuleAccess('RECLAMACIONES'));
  readonly hasConsultaModule = computed(() => this.hasModuleAccess('CONSULTA'));
  readonly hasAdministrativoModule = computed(() => this.hasModuleAccess('ADMINISTRATIVO'));
  readonly hasMantenimientoModule = computed(() => this.hasModuleAccess('MANTENIMIENTO'));
  readonly hasEmisionModule = computed(() => this.hasModuleAccess('EMISION'));

  // Permisos Específicos
  readonly canCotizarAuto = computed(() => this.hasPermission('COTIZACIONES', 'VEHICULOS_FULL_INICIAR_COTIZACION') || this.hasPermission('COTIZACIONES', 'VEHICULOS_LEY_INICIAR_COTIZACION'));
  readonly canCotizarSmi = computed(() => this.hasPermission('COTIZACIONES', 'SMI_INICIAR_COTIZACION'));
  readonly canCotizarArmaloTu = computed(() => this.hasPermission('COTIZACIONES', 'ARMALOTU_INICIAR_COTIZACION') || this.hasPermission('COTIZACIONES', 'ARMALOTU_CONSULTAR_IMPRIMIR'));
  readonly canDescuentoArmaloTu = computed(() => this.hasPermission('COTIZACIONES', 'DESCUENTO_ARMALOTU'));
  readonly canEmprendeSeguro = computed(() => this.hasPermission('COTIZACIONES', 'EMPRENDE_SEGURO_INICIAR') || this.hasPermission('COTIZACIONES', 'EMPRENDE_SEGURO_CONSULTAR'));
  readonly canHogarSeguro = computed(() => this.hasPermission('COTIZACIONES', 'HOGAR_SEGURO_INICIAR') || this.hasPermission('COTIZACIONES', 'HOGAR_SEGURO_CONSULTAR'));

  readonly canCreditCardPayment = computed(() => this.hasPermission('PAGOS', 'PAGOS_TARJETA_CREDITO'));
  readonly canReprintCardReceipts = computed(() => this.hasPermission('PAGOS', 'REIMPRESION_RECIBOS_TARJETA'));

  // Permisos Cobros
  readonly canViewClientAccountStatement = computed(() => this.hasPermission('COBROS', 'ESTADO_CUENTA_CLIENTE'));
  readonly canViewIntermediaryAccountStatement = computed(() => this.hasPermission('COBROS', 'ESTADO_CUENTA_INTERMEDIARIO'));
  readonly canViewPaymentVouchers = computed(() => this.hasPermission('COBROS', 'MIS_VOLANTES_PAGOS'));
  readonly canViewMyCharges = computed(() => this.hasPermission('COBROS', 'MIS_COBROS_CONSULTAR'));
  readonly canViewCommissions = computed(() => this.hasPermission('COBROS', 'MIS_COMISIONES'));
  readonly canViewAgingProducer = computed(() => this.hasPermission('COBROS', 'ANTIGUEDAD_POR_SALDO'));
  readonly canUpdateChargeStatus = computed(() => this.hasPermission('COBROS', 'MIS_COBROS_ACTUALIZAR_ESTADOS'));
  readonly canViewCobrosReports = computed(() =>
    this.canViewClientAccountStatement() ||
    this.canViewIntermediaryAccountStatement() ||
    this.canViewPaymentVouchers() ||
    this.canViewMyCharges() ||
    this.canViewCommissions() ||
    this.canViewAgingProducer()
  );

  readonly canViewStatisticsReports = computed(() => this.hasPermission('ESTADISTICAS', 'MIS_ESTADISTICAS'));
  readonly canViewBondGrowth = computed(() => this.hasPermission('ESTADISTICAS', 'PRIMAS_COBRADAS_BONO_CRECIMIENTO'));

  readonly canViewLossRatio = computed(() => this.hasPermission('REPORTE', 'REPORTE_SINIESTRALIDAD'));

  // Permisos Pólizas
  readonly canViewMyPolicies = computed(() => this.hasPermission('POLIZAS', 'MIS_POLIZAS'));
  readonly canViewPoliciesToRenew = computed(() => this.hasPermission('POLIZAS', 'MIS_POLIZAS_A_RENOVAR') || this.hasPermission('POLIZAS', 'POLIZAS_A_RENOVAR_EJECUTIVO'));
  readonly canViewPolicyCancel = computed(() => this.hasPermission('POLIZAS', 'POLIZAS_CANCELADAS'));
  readonly canViewMyProduction = computed(() => this.hasPermission('POLIZAS', 'MI_PRODUCCION'));
  readonly canViewPoliciesReports = computed(() =>
    this.canViewMyPolicies() ||
    this.canViewPoliciesToRenew() ||
    this.canViewPolicyCancel() ||
    this.canViewMyProduction()
  );

  readonly canViewMyClaims = computed(() => this.hasPermission('RECLAMACIONES', 'MIS_RECLAMACIONES'));

  readonly canViewClients = computed(() => this.hasPermission('CONSULTA', 'CONSULTA_CLIENTES_GENERAL'));

  readonly canViewInsureds = computed(() => this.hasPermission('ADMINISTRATIVO', 'CONSULTA_ASEGURADOS_GENERAL'));

  readonly canSafeSurgery = computed(() => this.hasPermission('MANTENIMIENTO', 'CIRUGIA_SEGURA'));
  readonly canViewMyUsers = computed(() => this.hasPermission('MANTENIMIENTO', 'MIS_USUARIOS'));
  readonly canEditMyUsers = computed(() => this.hasPermission('MANTENIMIENTO', 'MIS_USUARIOS_MODIFICAR'));

  constructor() {
    this.initializeAccountFromRedirect();

    this.msalBroadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.syncAccountState();
      });
  }

  hasPermission(moduleCode: string, permissionCode: string, appCode = 'COLOCLIC'): boolean {
    const perms = this._permissions();
    if (!perms || !perms.applications) return false;
    const app = perms.applications.find((a) => a.code.toUpperCase() === appCode.toUpperCase());
    if (!app) return false;
    const mod = app.modules.find((m) => m.code.toUpperCase() === moduleCode.toUpperCase());
    if (!mod) return false;
    return mod.permissions.some((p) => p.code.toUpperCase() === permissionCode.toUpperCase());
  }

  hasModuleAccess(moduleCode: string, appCode = 'COLOCLIC'): boolean {
    const perms = this._permissions();
    if (!perms || !perms.applications) return false;
    const app = perms.applications.find((a) => a.code.toUpperCase() === appCode.toUpperCase());
    if (!app) return false;
    const mod = app.modules.find((m) => m.code.toUpperCase() === moduleCode.toUpperCase());
    return !!mod && mod.permissions && mod.permissions.length > 0;
  }

  hasAppAccess(appCode = 'COLOCLIC'): boolean {
    const perms = this._permissions();
    if (!perms || !perms.applications) return false;
    return perms.applications.some((a) => a.code.toUpperCase() === appCode.toUpperCase());
  }

  private async initializeAccountFromRedirect() {
    try {
      await this.aadMsalInstance.initialize();

      const b2cResult = await this.msal.instance.handleRedirectPromise();
      const aadResult = await this.aadMsalInstance.handleRedirectPromise();

      if (b2cResult?.account) {
        this.msal.instance.setActiveAccount(b2cResult.account);
      } else if (aadResult?.account) {
        this.aadMsalInstance.setActiveAccount(aadResult.account);
      } else {
        this.ensureActiveAccount();
      }

      this._isInitialized.set(true);
      await this.syncAccountState();
    } catch (error) {
      console.error('[AuthService] Error initializing account from redirect:', error);
      this._isInitialized.set(true);
      await this.syncAccountState();
    }
  }

  private ensureActiveAccount() {
    if (this.msal.instance.getActiveAccount() || this.aadMsalInstance.getActiveAccount()) {
      return;
    }

    const aadAccounts = this.aadMsalInstance.getAllAccounts();
    if (aadAccounts.length > 0) {
      this.aadMsalInstance.setActiveAccount(aadAccounts[0]);
      return;
    }

    const b2cAccounts = this.msal.instance.getAllAccounts();
    if (b2cAccounts.length > 0) {
      this.msal.instance.setActiveAccount(b2cAccounts[0]);
    }
  }

  login(returnUrl?: string) {
    const extraQueryParameters = returnUrl ? { returnUrl } : undefined;
    return this.msal.loginRedirect({
      authority: environment.b2c.authorities.signUpSignIn.authority,
      scopes: environment.b2c.apiScopes,
      extraQueryParameters,
    });
  }

  async loginInternal(returnUrl?: string) {
    const extraQueryParameters = returnUrl ? { returnUrl } : undefined;
    return this.aadMsalInstance.loginRedirect({
      scopes: environment.aad.apiScopes,
      extraQueryParameters,
      authority: environment.aad.authority,
    });
  }

  register(returnUrl?: string) {
    const extraQueryParameters = returnUrl ? { returnUrl } : undefined;
    return this.msal.loginRedirect({
      authority: environment.b2c.authorities.signUp.authority,
      scopes: environment.b2c.apiScopes,
      extraQueryParameters,
    });
  }

  resetPassword() {
    return this.msal.loginRedirect({
      authority: environment.b2c.authorities.resetPassword.authority,
      scopes: environment.b2c.apiScopes,
    });
  }

  editProfile() {
    return this.msal.loginRedirect({
      authority: environment.b2c.authorities.editProfile.authority,
      scopes: environment.b2c.apiScopes,
    });
  }

  logout() {
    this._user.set(null);
    this._authStatus.set('not-authenticated');
    this._permissions.set(null);
    this._permissionsLoaded.set(false);
    
    localStorage.removeItem('token');

    const b2cAccount = this.msal.instance.getActiveAccount();
    if (b2cAccount) {
      return this.msal.logoutRedirect({
        postLogoutRedirectUri: environment.b2c.postLogoutRedirectUri,
      });
    }

    const aadAccount = this.aadMsalInstance.getActiveAccount();
    if (aadAccount) {
      return this.aadMsalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/',
      });
    }

    window.location.href = '/';
    return Promise.resolve();
  }

  async acquireToken(): Promise<string | null> {
    const { account, isInternal } = this.getActiveAccountDetail();
    if (!account) return null;

    const silentRequest: SilentRequest = {
      account,
      scopes: isInternal ? environment.aad.apiScopes : environment.b2c.apiScopes,
      authority: isInternal ? environment.aad.authority : environment.b2c.authorities.signUpSignIn.authority,
    };

    try {
      const instance = isInternal ? this.aadMsalInstance : this.msal.instance;
      const result = await instance.acquireTokenSilent(silentRequest);
      return result.accessToken ?? null;
    } catch {
      return null;
    }
  }

  private async syncAccountState() {
    if (!this._isInitialized()) {
      return;
    }

    const { account, isInternal } = this.getActiveAccountDetail();

    if (!account) {
      this._user.set(null);
      this._authStatus.set('not-authenticated');
      this._permissions.set(null);
      this._permissionsLoaded.set(false);
      localStorage.removeItem('token');
      return;
    }

    const claims = (account.idTokenClaims as any) ?? {};
    const displayName =
      claims.name ??
      [claims.given_name, claims.family_name].filter(Boolean).join(' ') ??
      account.name ??
      null;

    const producerNumber = !isInternal ? (
      claims.extension_ProducerNumber ||
      claims['extension_ProducerNumber'] ||
      null
    ) : null;

    const finalProducerNumber = producerNumber ? Number(producerNumber) : 0;

    const userObj: AppUser = {
      name: displayName,
      username: (account.username && account.username.trim() !== '') ? account.username : (claims.emails?.[0] ?? claims.upn ?? null),
      producerNumber: finalProducerNumber,
      isInternal,
      claims,
    };

    this._user.set(userObj);
    this._authStatus.set('authenticated');

    await this.saveMsalTokenToLocalStorage(account, isInternal);
    await this.loadUserPermissions(userObj);
  }

  private async loadUserPermissions(user: AppUser): Promise<void> {
    if (this._isLoadingPermissions) return;
    this._isLoadingPermissions = true;

    // Mostrar el overlay de carga con el mensaje indicando la validación de accesos
    this.loadingService.show('Validando accesos...');

    try {
      let rolesToSend: string[] = [];

      if (!user.isInternal) {
        rolesToSend = ['Intermediario'];
      } else {
        const claims = user.claims ?? {};
        const claimsRoles = claims['roles'];
        const claimsRole = claims['role'];
        if (Array.isArray(claimsRoles)) {
          rolesToSend = claimsRoles;
        } else if (typeof claimsRoles === 'string') {
          rolesToSend = claimsRoles.split(',').map((r: string) => r.trim()).filter(Boolean);
        } else if (Array.isArray(claimsRole)) {
          rolesToSend = claimsRole;
        } else if (typeof claimsRole === 'string') {
          rolesToSend = claimsRole.split(',').map((r: string) => r.trim()).filter(Boolean);
        }

        if (rolesToSend.length === 0) {
          console.warn('[AuthService] Usuario AAD interno sin claim "roles" en token. Usando roles por defecto.');
          rolesToSend = ['GerenteArea', 'Ejecutivo', 'Analista', 'AgenteVentas', 'GerenteCobros', 'OficialAdmCobros', 'AsistenteAdmCobros'];
        }
      }

      if (rolesToSend.length === 0) {
        this._permissions.set(null);
        return;
      }

      const response = await firstValueFrom(this.securityService.getPermissions(rolesToSend));
      if (response && response.succeeded && response.data) {
        this._permissions.set(response.data);
      } else {
        console.warn('[AuthService] getPermissions devolvió respuesta vacía o no exitosa');
        this._permissions.set(null);
      }
    } catch (error) {
      console.error('[AuthService] Error al cargar permisos:', error);
      this._permissions.set(null);
    } finally {
      this.loadingService.hide();
      this._permissionsLoaded.set(true);
      this._isLoadingPermissions = false;
    }
  }

  private async saveMsalTokenToLocalStorage(account: AccountInfo, isInternal: boolean): Promise<string | null> {
    try {
      const silentRequest: SilentRequest = {
        account,
        scopes: isInternal ? environment.aad.apiScopes : environment.b2c.apiScopes,
        authority: isInternal ? environment.aad.authority : environment.b2c.authorities.signUpSignIn.authority,
      };

      const instance = isInternal ? this.aadMsalInstance : this.msal.instance;
      const result = await instance.acquireTokenSilent(silentRequest);
      
      if (result?.accessToken) {
        localStorage.setItem('token', result.accessToken);
        return result.accessToken;
      } else {
        localStorage.removeItem('token');
        return null;
      }
    } catch (error) {
      console.error('[AuthService] Error al obtener token de MSAL:', error);
      return null;
    }
  }

  private getActiveAccountDetail(): { account: AccountInfo | null, isInternal: boolean } {
    const aadActive = this.aadMsalInstance.getActiveAccount();
    if (aadActive) return { account: aadActive, isInternal: true };

    const b2cActive = this.msal.instance.getActiveAccount();
    if (b2cActive) return { account: b2cActive, isInternal: false };

    const aadAll = this.aadMsalInstance.getAllAccounts();
    if (aadAll.length > 0) {
      this.aadMsalInstance.setActiveAccount(aadAll[0]);
      return { account: aadAll[0], isInternal: true };
    }

    const b2cAll = this.msal.instance.getAllAccounts();
    if (b2cAll.length > 0) {
      this.msal.instance.setActiveAccount(b2cAll[0]);
      return { account: b2cAll[0], isInternal: false };
    }

    return { account: null, isInternal: false };
  }
}